/** Shared by `src/app/api/inquiries/route.ts` (dev) and `hostinger/api/inquiries/index.php` (export). */
import { INQUIRY_TYPE_IDS, isInquiryTypeId, type InquiryTypeId } from './inquiryTypes';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MAX = {
  name: 120,
  email: 254,
  company: 160,
  phone: 40,
  message: 8000,
  source: 500,
  locale: 8,
} as const;

export type InquiryPayload = {
  type: InquiryTypeId;
  name: string;
  email: string;
  message: string;
  company?: string;
  phone?: string;
  source?: string;
  locale?: string;
};

/** Bot trap — obscure name; avoid `website`, `fax`, `email`, `url` (browsers autofill those). */
export const INQUIRY_HONEYPOT_FIELD = 'inquiry_ref_alt';

export type ValidationResult =
  | { ok: true; data: InquiryPayload }
  | { ok: false; error: string; status: number };

function trimOpt(value: unknown, max: number): string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value !== 'string') return undefined;
  const t = value.trim();
  return t ? t.slice(0, max) : undefined;
}

export function parseInquiryBody(body: unknown): ValidationResult {
  if (!body || typeof body !== 'object') {
    return { ok: false, error: 'Invalid JSON body', status: 400 };
  }

  const raw = body as Record<string, unknown>;

  const typeRaw = typeof raw.type === 'string' ? raw.type.trim() : '';
  if (!isInquiryTypeId(typeRaw)) {
    return {
      ok: false,
      error: `type must be one of: ${INQUIRY_TYPE_IDS.join(', ')}`,
      status: 400,
    };
  }

  const name = typeof raw.name === 'string' ? raw.name.trim().slice(0, MAX.name) : '';
  const email = typeof raw.email === 'string' ? raw.email.trim().slice(0, MAX.email) : '';
  const message =
    typeof raw.message === 'string' ? raw.message.trim().slice(0, MAX.message) : '';

  if (!name) return { ok: false, error: 'name is required', status: 400 };
  if (!email || !EMAIL_RE.test(email)) {
    return { ok: false, error: 'valid email is required', status: 400 };
  }
  if (!message || message.length < 10) {
    return { ok: false, error: 'message must be at least 10 characters', status: 400 };
  }

  return {
    ok: true,
    data: {
      type: typeRaw,
      name,
      email,
      message,
      company: trimOpt(raw.company, MAX.company),
      phone: trimOpt(raw.phone, MAX.phone),
      source: trimOpt(raw.source, MAX.source),
      locale: trimOpt(raw.locale, MAX.locale),
    },
  };
}
